/**
 * Lift2Fit — the lifter dashboard (the post-login big picture).
 *
 * Big-picture summaries, never a spreadsheet. Every headline number takes its
 * label from the training track, so a wellness client never sees tonnage and a
 * conditioning client never sees a barbell metric they did not train.
 */
import { useEffect } from 'react';
import { api, type LifterDashboard as LifterDashboardPayload } from '@/lib/api';
import { formatDate, pluralize } from '@/lib/format';
import { formatVolume, formatWeight } from '@/lib/units';
import { useSession } from '@/lib/useSession';
import { useUnitPreference } from '@/lib/useUnitPreference';
import {
  ArrowRightIcon,
  BarbellIcon,
  CheckIcon,
  FlameIcon,
  GridIcon,
  LockIcon,
  ShieldIcon,
  SparkIcon,
  TargetIcon,
  TrendDownIcon,
  TrendUpIcon,
} from '@/lib/icons';
import {
  AlertChips,
  AppLinkButton,
  EmptyState,
  HighlightList,
  LoadingPanel,
  MetricTile,
  Panel,
  PersonaBadge,
  ScreenHeader,
  StatusChip,
  TrackBadge,
  TrendBars,
  WeekStrip,
  headlineMetrics,
} from '../components';
import { trackMeta } from '../demoData';
import { SourceNotice } from '../blocks';
import { YourCoachCard } from '../coach';
import { useCachedAsync } from '../hooks';
import { UpcomingSessionCard } from '../schedule';
import { Link } from '../router';

function greetingWord(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

const HIGHLIGHT_TITLES: Record<string, string> = {
  conditioning: 'Recent interval blocks',
  performance: 'Recent power markers',
  wellness: 'Recent check-ins',
};

export function LifterDashboard() {
  const session = useSession();
  const { unit } = useUnitPreference();
  const track = session.user?.track ?? 'general';
  const state = useCachedAsync(`me/dashboard:${track}`, () => api.dashboard('lifter'), [track]);
  const data = state.data && state.data.role === 'lifter' ? (state.data as LifterDashboardPayload) : null;
  const meta = trackMeta(data?.track ?? track);

  // The auth payload omits the training track (only the dashboard carries it).
  // Hydrate it once so the profile, the nav copy and the fallback dataset agree.
  const resolvedTrack = data?.track;
  const resolvedTrackLabel = data?.trackLabel;
  const sessionTrack = session.user?.track;
  const sessionIsApi = session.source === 'api';
  useEffect(() => {
    // Only a real API session: the demo identity's track is authoritative and
    // changes through the header switcher, so never overwrite it here.
    if (sessionIsApi && resolvedTrack && resolvedTrack !== sessionTrack) {
      session.updateUser({ track: resolvedTrack, trackLabel: resolvedTrackLabel ?? trackMeta(resolvedTrack).label });
    }
    // `updateUser` is re-created each render; the track comparison above is the guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIsApi, resolvedTrack, resolvedTrackLabel, sessionTrack]);

  const content = (() => {
    if (state.loading && !data) {
      return (
        <>
          <LoadingPanel label="Loading your week" rows={4} />
          <LoadingPanel label="Loading your numbers" rows={4} />
        </>
      );
    }
    if (!data) {
      return (
        <EmptyState
          title="We could not read your dashboard"
          body={state.error ?? 'The API did not answer and there is no demo payload for this role.'}
          icon={<BarbellIcon className="h-8 w-8 text-accent" />}
          action={
            <AppLinkButton to="/grid" variant="outline">
              Open The Grid
            </AppLinkButton>
          }
        />
      );
    }

    const tiles = headlineMetrics(data.track, data.trackMetrics, unit, data.totals);
    const coach = data.coach;
    const highlights = data.highlights ?? [];
    const useHighlights = highlights.length > 0;
    const highlightTitle =
      HIGHLIGHT_TITLES[data.track] ?? (data.track === 'strength' || data.track === 'hypertrophy' ? 'Recent PRs' : 'Recent highlights');

    return (
      <>
        <ScreenHeader
          eyebrow={`${greetingWord()}, ${data.greeting.displayName.split(' ')[0]}`}
          title={data.trackLabel}
          badge={<TrackBadge track={data.track} />}
          lead={`${meta.persona} · your ${meta.sessionWord}s, this week at a glance.`}
          actions={
            <span className="flex flex-wrap items-center gap-2">
              <StatusChip tone="accent" icon={<FlameIcon />}>
                {pluralize(data.streak.currentWeeks, 'week')} streak
              </StatusChip>
              <StatusChip tone="neutral">Best {data.streak.longestWeeks}</StatusChip>
            </span>
          }
        />

        <nav aria-label="Block" className="flex flex-wrap items-center gap-2">
          <Link
            to="/grid"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            <GridIcon className="text-sm" />
            My Grid
            <ArrowRightIcon />
          </Link>
          <Link
            to="/programme"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            <BarbellIcon className="text-sm" />
            My Programme
          </Link>
          <Link
            to="/summary"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            <GridIcon className="text-sm" />
            Block summary
            <ArrowRightIcon />
          </Link>
          <Link
            to="/goals"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            <TargetIcon className="text-sm" />
            Block goals
          </Link>
          <Link
            to="/my-coach"
            data-testid="dashboard-manage-coach"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            <ShieldIcon className="text-sm" />
            Manage coach
            <ArrowRightIcon />
          </Link>
        </nav>

        <SourceNotice source={state.source} isDemoSession={session.isDemo} />

        {data.alerts.length ? (
          <div
            className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
            data-testid="dashboard-reminder-row"
          >
            <Panel
              title="Coach alerts"
              subtitle="Colour + icon + word — never colour alone."
              testId="coach-alerts"
            >
              <AlertChips alerts={data.alerts} />
            </Panel>
            <UpcomingSessionCard />
          </div>
        ) : (
          // No alerts: the reminder keeps its place at the top of the dashboard
          // (and its full width) rather than looking orphaned beside a gap.
          <UpcomingSessionCard />
        )}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <Panel
            title="Your week"
            subtitle={`${data.week.completed} of ${pluralize(data.week.target, meta.sessionWord)} logged`}
            actions={
              <StatusChip tone={data.streak.qualifiesThisWeek ? 'success' : 'warning'} icon={data.streak.qualifiesThisWeek ? <CheckIcon /> : <TrendDownIcon />}>
                {data.streak.qualifiesThisWeek ? 'Streak safe' : 'Streak at risk'}
              </StatusChip>
            }
          >
            <WeekStrip week={data.week} />
          </Panel>

          <Panel
            title="Next session"
            subtitle={
              data.nextSession
                ? `What to train next · last performed ${formatDate(data.nextSession.lastPerformed ?? data.greeting.localDate)}`
                : 'What to train next'
            }
          >
            {data.nextSession ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-[22px] font-black uppercase leading-none text-ink">
                    {data.nextSession.dayLabel}
                  </span>
                  {data.nextSession.focus.map((focus) => (
                    <StatusChip key={focus} tone="neutral">
                      {focus}
                    </StatusChip>
                  ))}
                </div>
                <div className="flex flex-wrap gap-6">
                  <span className="flex flex-col">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Exercises</span>
                    <span className="l2f-tabular font-mono text-xl font-bold text-ink">{data.nextSession.exercises}</span>
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Working sets</span>
                    <span className="l2f-tabular font-mono text-xl font-bold text-ink">{data.nextSession.sets}</span>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <AppLinkButton to="/grid" size="lg">
                    Start session
                    <ArrowRightIcon />
                  </AppLinkButton>
                  <span className="text-[12px] text-ink-muted">Opens The Grid — one tap per set.</span>
                </div>
              </div>
            ) : (
              <EmptyState
                title="No programme day ready"
                body={`${meta.emptyAction} and the coach will pick the block up from your log.`}
                icon={<SparkIcon className="h-8 w-8 text-accent" />}
                action={
                  <AppLinkButton to="/grid" variant="outline">
                    {meta.emptyAction}
                  </AppLinkButton>
                }
              />
            )}
          </Panel>
        </div>

        <section aria-label="Headline numbers" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {tiles.map((tile) => (
            <MetricTile
              key={tile.label}
              label={tile.label}
              value={tile.value}
              valueTitle={tile.title}
              hint={tile.hint}
              tone={tile.tone}
              hintTone={tile.hintTone}
            />
          ))}
        </section>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <Panel title="8-week trend" subtitle={`Whatever your track tracks — not a spreadsheet.`}>
            {data.trend.length ? (
              <TrendBars points={data.trend} unit={unit} />
            ) : (
              <EmptyState
                title="Not enough history yet"
                body={`Log two weeks of ${meta.sessionWord}s and the trend appears here.`}
                icon={<TrendUpIcon className="h-8 w-8 text-accent" />}
              />
            )}
          </Panel>

          <Panel
            title={highlightTitle}
            subtitle={useHighlights ? 'The last five things worth knowing.' : 'Top 5 recent personal records.'}
          >
            {useHighlights ? (
              <HighlightList highlights={highlights.slice(0, 5)} />
            ) : data.personalRecords.length ? (
              <ul className="flex flex-col divide-y divide-hairline">
                {data.personalRecords.slice(0, 5).map((record) => (
                  <li key={`${record.exerciseName}-${record.achievedAt}`} className="flex items-baseline justify-between gap-4 py-2.5">
                    <span className="flex min-w-0 flex-col">
                      <span className="text-[14px] font-bold text-ink">{record.exerciseName}</span>
                      <span className="text-[12px] text-ink-muted">{formatDate(record.achievedAt)}</span>
                    </span>
                    <span className="l2f-tabular shrink-0 font-mono text-[14px] font-bold text-ink">
                      {formatWeight(record.weightKg, unit)} × {record.reps}
                      <span className="ml-2 text-[12px] font-normal text-ink-muted">
                        e1RM {formatWeight(record.estimated1rmKg, unit)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title={data.track === 'wellness' ? 'No check-ins yet' : 'No records yet'}
                body={
                  data.track === 'wellness'
                    ? 'Log a mobility session and your pain score — consistency is the score here, not tonnage.'
                    : `${meta.emptyAction} and the first record lands here.`
                }
                icon={<CheckIcon className="h-8 w-8 text-accent" />}
              />
            )}
          </Panel>
        </div>

        <Panel
          title="Your coach"
          subtitle={`${coach.persona} · ${coach.latestReportAt ? `last report ${formatDate(coach.latestReportAt)}` : 'no report yet'}`}
          actions={<PersonaBadge track={data.track} persona={coach.persona} />}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 flex-col gap-2">
              {coach.verdict ? (
                <StatusChip tone={coach.hasPremium ? 'success' : 'accent'} icon={coach.hasPremium ? <CheckIcon /> : <LockIcon />}>
                  {coach.verdict}
                </StatusChip>
              ) : null}
              <p className="max-w-[70ch] text-[15px] text-ink">
                {coach.headline ??
                  'Your log has a coach in it. Generate a plan and it will read every set you have logged.'}
              </p>
              {coach.changes ? (
                <p className="text-[13px] text-ink-muted">
                  <span className="l2f-tabular font-mono font-bold text-ink">{coach.changes}</span> prescribed changes waiting
                  in the next block.
                </p>
              ) : null}
            </div>

            <div className="flex shrink-0 flex-col items-start gap-2">
              {coach.hasPremium ? (
                <Link
                  to="/plan/new"
                  className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                >
                  Plan my next block
                  <ArrowRightIcon />
                </Link>
              ) : (
                <>
                  <span className="max-w-[36ch] text-[13px] text-ink-muted">{meta.coachPitch}</span>
                  <Link
                    to="/plan/new"
                    className="inline-flex h-11 items-center gap-2 bg-accent px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
                  >
                    Generate my plan
                    <ArrowRightIcon />
                  </Link>
                  <span className="text-[11px] uppercase tracking-[0.08em] text-ink-muted">
                    Free trial · £3.99/month
                  </span>
                </>
              )}
            </div>
          </div>
        </Panel>

        <YourCoachCard />

        <p className="text-[12px] text-ink-muted">
          {data.track === 'wellness'
            ? `${data.trackMetrics.mobilitySessions ?? 0} mobility sessions · pain ${data.trackMetrics.painScore ?? '—'}/10 · ${data.trackMetrics.feel ?? 'no check-in yet'}`
            : data.track === 'conditioning'
              ? `${data.trackMetrics.intervalMinutes ?? 0} interval minutes · work:rest ${data.trackMetrics.workRestRatio ?? '—'} · ${(data.trackMetrics.ergMetres ?? 0).toLocaleString('en-GB')} erg metres`
              : `Weekly volume ${formatVolume(data.totals.volumeKg7, unit)} · ${data.totals.sets7} sets · ${
                  data.totals.avgRpe7 === null ? 'RPE not logged' : `avg RPE ${data.totals.avgRpe7}`
                }`}
        </p>
      </>
    );
  })();

  return <div className="flex flex-col gap-6">{content}</div>;
}
